import { useEffect } from 'react';
import type { FC } from 'react';
import { TweenMax } from 'gsap';
import css from './appCustom.module.scss';
import { supportLinkLocales } from './const';
import { useGlobal } from '@/utils/useGlobal';
import EFVideo from '@/components/UI/EFVideo';
import NextImage from '@/components/NextImage';
import EFArrowRightBlueSmall from '@/components/UI/svg/Icons/EFArrowRightBlueSmall';

const TRIGGER_CLS = 'appCustom';
const FEAT_KEY = {
    backup: 'backup',
    smart: 'smart',
    speed: 'speed',
} as const;
const APP_LINK = '/app';

const AppCustom: FC = () => {
    const { useLang, useRouter, Link } = useGlobal();
    const { locale } = useRouter();
    const { t } = useLang('delta2portablepowerstation.delta2');
    const layoutH = typeof window !== 'undefined' ? window.innerHeight : 1042;
    const showAppLink = supportLinkLocales.includes(locale);

    const featList = [
        { key: FEAT_KEY.backup, title: t('app_custom_backup_title'), desc: t('app_custom_backup_desc'), img: getLocaleNextImage(locale, FEAT_KEY.backup) },
        { key: FEAT_KEY.smart, title: t('app_custom_smart_title'), desc: t('app_custom_smart_desc'), img: getLocaleNextImage(locale, FEAT_KEY.smart) },
        { key: FEAT_KEY.speed, title: t('app_custom_speed_title'), desc: t('app_custom_speed_desc'), img: getLocaleNextImage(locale, FEAT_KEY.speed) },
    ];

    useEffect(() => {
        const sceneA1 = new ScrollMagic.Scene({
            triggerElement: `.${TRIGGER_CLS}`,
            offset: layoutH * 0.4, // 从开始点滚动多少px触发,可为负数
            triggerHook: 1, // 触发元素开始进入视口时触发:1 进入,0 离开, 0.5 中间,
            duration: '30%', // 和滚动条绑定到一起,场景持续滚动距离为 800px
        });

        const tweenA1 = TweenMax.fromTo(
            `.${TRIGGER_CLS} .msg-box`,
            0.5,
            {
                opacity: 0,
                ease: 'ease-out',
            },
            {
                opacity: 1,
                ease: 'ease-out',
            },
        );

        sceneA1.setTween(tweenA1);
        // @ts-ignore window.controllerDelta2
        controllerDelta2?.addScene(sceneA1);
    }, []);

    return (
        <div className={`${css.appCustom} ${TRIGGER_CLS} GtmModuleData-section`} data-section-id={css.appCustom} data-section-title={t('app_custom_title')}>
            <div className="static-box">
                <div className="msg-box">
                    <div className="msg-title">{t('app_custom_title')}</div>
                    <div className="msg-desc">{t('app_custom_description')}</div>
                </div>
                <div className="video-box">
                    <EFVideo className="video" sources={getVideoSource(locale)} />
                    <div className="video-box-content">
                        <p className="video-box-desc">{t('app_custom_video_desc')}</p>
                        {showAppLink && (
                            <Link href={APP_LINK} className="learn-more-wrapper" newWin>
                                <div>{t('learn_more')}</div>
                                <EFArrowRightBlueSmall />
                            </Link>
                        )}
                    </div>
                </div>
                <div className="feat-box">
                    {featList.map((featItem) => (
                        <div className="feat-box-item" key={featItem.key}>
                            <div className="feat-box-img">{featItem.img}</div>
                            <div className="feat-box-title">{featItem.title}</div>
                            <div className="feat-box-desc">{featItem.desc}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const getLocaleNextImage = (locale: string, key: keyof typeof FEAT_KEY) => {
    let webpSrc = '';
    let src = '';

    switch (locale) {
        case 'jp': {
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-jp.webp');
                    src = require('@publicLocal/delta2/common/feat-a-jp.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-jp.webp');
                    src = require('@publicLocal/delta2/common/feat-b-jp.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-jp.webp');
                    src = require('@publicLocal/delta2/common/feat-c-jp.png');
            }
            break;
        }
        case 'fr': {
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-fr.webp');
                    src = require('@publicLocal/delta2/common/feat-a-fr.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-fr.webp');
                    src = require('@publicLocal/delta2/common/feat-b-fr.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-fr.webp');
                    src = require('@publicLocal/delta2/common/feat-c-fr.png');
            }
            break;
        }

        case 'de': {
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-de.webp');
                    src = require('@publicLocal/delta2/common/feat-a-de.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-de.webp');
                    src = require('@publicLocal/delta2/common/feat-b-de.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-de.webp');
                    src = require('@publicLocal/delta2/common/feat-c-de.png');
            }
            break;
        }
        case 'es': {
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-es.webp');
                    src = require('@publicLocal/delta2/common/feat-a-es.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-es.webp');
                    src = require('@publicLocal/delta2/common/feat-b-es.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-es.webp');
                    src = require('@publicLocal/delta2/common/feat-c-es.png');
            }
            break;
        }
        case 'it': {
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-it.webp');
                    src = require('@publicLocal/delta2/common/feat-a-it.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-it.webp');
                    src = require('@publicLocal/delta2/common/feat-b-it.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-it.webp');
                    src = require('@publicLocal/delta2/common/feat-c-it.png');
            }
            break;
        }
        default:
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-en.webp');
                    src = require('@publicLocal/delta2/common/feat-a-en.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-en.webp');
                    src = require('@publicLocal/delta2/common/feat-b-en.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-en.webp');
                    src = require('@publicLocal/delta2/common/feat-c-en.png');
            }
    }

    return <NextImage webpSrc={webpSrc} src={src} alt={FEAT_KEY[key]} />;
};

const getVideoSource = (locale: string) => {
    const arr = ['fr', 'de', 'it', 'es', 'jp'];
    let imgLoc = 'en';
    if (arr.includes(locale)) {
        imgLoc = locale;
    }
    return [`/media/delta2/pc/app-custom/animk-${imgLoc}.webm`, `/media/delta2/pc/app-custom/animk-${imgLoc}.mp4`];
};

export default AppCustom;
